import React, { useMemo } from "react";
import "./Disclaimer.css";
import { useLocalContext } from "../../context/LocalContext";

const Disclaimer = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "digivirtuehost.com",
      email: webinfo.email || "contact@digivirtuehost.com",
      phone: webinfo.phone || "+1 (000) 000-0000",
      phonecall: webinfo.phonecall || "+10000000000",
      address: webinfo.address || "United States",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const sections = useMemo(
    () => [
      {
        title: "1. General — informational use only",
        text: `Information on this website is provided for general informational purposes
              under U.S. law and is not tailored to your specific legal, financial, or
              technical situation. ${brand.name} uses reasonable efforts to keep
              descriptions of services, features, and pricing accurate, but errors may
              occur. We may change site content, product offerings, or terms at any time.
              The “last updated” date reflects the latest revision of this Disclaimer.`,
      },
      {
        title: "2. No professional advice",
        text: `Nothing on this website constitutes legal, tax, accounting, or
              professional advice under federal or state law. Hosting, security, and
              compliance obligations depend on your industry, data, and jurisdiction.
              You should consult qualified professionals before relying on any
              information here for decisions that could affect your business or legal
              obligations.`,
      },
      {
        title: "3. Hosting, uptime & data",
        text: `Unless a separate written Service Level Agreement (SLA) applies, we do not
              guarantee uninterrupted or error-free service, specific uptime
              percentages, or recovery of data. Internet and hosting services are
              subject to outages, maintenance, third-party failures, and force majeure
              events. You are responsible for maintaining backups and disaster recovery
              appropriate to your needs unless your plan expressly includes backup
              services. See our Terms of Service for limitations of liability.`,
      },
      {
        title: "4. No warranty (U.S. law)",
        text: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE U.S. LAW, THIS WEBSITE AND
              ITS CONTENT ARE PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES
              OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. SOME U.S.
              STATES DO NOT ALLOW CERTAIN DISCLAIMERS; IN THOSE STATES, DISCLAIMERS ARE
              LIMITED TO THE EXTENT PERMITTED BY LAW.`,
      },
      {
        title: "5. Limitation of liability",
        text: `TO THE FULLEST EXTENT PERMITTED BY U.S. LAW, ${brand.name} AND ITS
              OFFICERS, EMPLOYEES, AND SUPPLIERS WILL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
              PROFITS, DATA, OR BUSINESS, ARISING FROM YOUR USE OF THIS WEBSITE OR
              RELIANCE ON ITS CONTENT, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
              DAMAGES. YOUR SOLE REMEDY FOR DISSATISFACTION WITH THE SITE IS TO STOP
              USING IT. CERTAIN STATE LAWS DO NOT ALLOW LIMITATIONS; WHERE PROHIBITED,
              OUR LIABILITY IS LIMITED TO THE MINIMUM AMOUNT ALLOWED BY LAW.`,
      },
      {
        title: "6. FTC & advertising disclosures",
        text: `If we display testimonials, case studies, or performance metrics, they
              reflect past experiences under specific conditions and are not a guarantee
              of future results, including uptime, speed, or search rankings. We comply
              with the Federal Trade Commission (FTC) Act and related guidance on
              truthful advertising; material connections between ${brand.name} and
              endorsers will be disclosed where required.`,
      },
      {
        title: "7. Third-party links & services",
        text: `Links to third-party websites, registrars, CDNs, or tools are provided
              for convenience. ${brand.name} does not control and is not responsible
              for third-party content, privacy practices, or availability. Your use of
              third-party services is governed by their terms and policies.`,
      },
      {
        title: "8. Copyright & DMCA",
        text: `We respect intellectual property rights. Notices of alleged copyright
              infringement under the U.S. Digital Millennium Copyright Act (DMCA) should
              be sent to the contact email below in accordance with 17 U.S.C. § 512.
              Misrepresentations in a notice may subject you to liability under federal
              law.`,
      },
      {
        title: "9. Forward-looking statements",
        text: `Any statements about future products, features, or roadmap items are
              forward-looking and subject to change. We undertake no obligation to
              update such statements except as required by U.S. securities law (if
              applicable) or other applicable law.`,
      },
      {
        title: "10. Governing law",
        text: `This Disclaimer is governed by the laws of the United States and the
              State of Delaware, consistent with our Terms of Service, without regard to
              conflict-of-law principles, except where mandatory consumer protections in
              your state of residence apply.`,
      },
      {
        title: "11. Changes",
        text: `We may update this Disclaimer from time to time. Continued use of the
              website after changes are posted constitutes acceptance of the revised
              Disclaimer, to the extent permitted by applicable U.S. law.`,
      },
    ],
    [brand.name]
  );

  return (
    <section className="disclaimerv2">
      <div className="dv2-wrap">
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Legal notice — United States</p>
          <div className="pp-header-main">
            <h1>Disclaimer</h1>
          </div>
          <p className="pp-meta">
            <span>Issued by: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
          </p>
          <p className="dv2-lead">
            This Disclaimer supplements our{" "}
            <a href="/terms-and-condition">Terms of Service</a> and{" "}
            <a href="/privacy-policy">Privacy Policy</a>. Please read them together.
          </p>
        </header>

        <div className="privacy-policy-content">
          {sections.map((sec, idx) => (
            <section key={idx}>
              <h2>{sec.title}</h2>
              <p>{sec.text}</p>
            </section>
          ))}

          <section>
            <h2>12. Contact</h2>
            <p>
              Questions about this Disclaimer or legal notices under U.S. law may be
              directed to:
            </p>
            <ul className="pp-contact-list">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
              </li>
              <li>
                <strong>Mailing address:</strong> {brand.address}
              </li>
            </ul>
            <p className="dv2-footnote">
              This page is provided for transparency and does not create a
              lawyer–client relationship. For legal advice about your specific
              situation, consult a licensed attorney in your jurisdiction.
            </p>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Disclaimer;
